import { useState, useCallback, useEffect } from "react";
import { debounce } from "lodash";
import useData from "./useData";

const useClientDashboard = () => {
  const { executeAction } = useData();

  // Filter state
  const [filters, setFilters] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    startDate: null,
    endDate: null,
    clientId: null,
  });

  // Data state for each metric
  const [unbilledByClient, setUnbilledByClient] = useState({
    data: null,
    isLoading: false,
    error: null,
  });
  const [ytdInvoicing, setYtdInvoicing] = useState({
    data: null,
    isLoading: false,
    error: null,
  });
  const [callsDetail, setCallsDetail] = useState({
    data: [],
    page: 1,
    pageSize: 25,
    total: 0,
    isLoading: false,
    error: null,
  });
  const [invoicesDetail, setInvoicesDetail] = useState({
    data: [],
    page: 1,
    pageSize: 25,
    total: 0,
    isLoading: false,
    error: null,
  });

  // Update filters with debounce to avoid excessive API calls
  const updateFilters = useCallback(
    debounce((newFilters) => {
      setFilters((prev) => ({ ...prev, ...newFilters }));
    }, 500),
    [],
  );

  useEffect(() => {
    return () => {
      updateFilters.cancel();
    };
  }, [updateFilters]);

  // ===== UNBILLED BY CLIENT =====
  const getUnbilledByClient = useCallback(
    async (customFilters = null) => {
      setUnbilledByClient((prev) => ({
        ...prev,
        isLoading: true,
        error: null,
      }));
      try {
        const filterParams = customFilters || filters;
        const result = await executeAction({
          entityName: "Dashboard",
          action: "unbilledByClient",
          data: filterParams,
        });

        if (result?.isSuccessful && result?.data) {
          setUnbilledByClient((prev) => ({
            ...prev,
            data: result.data || [],
            isLoading: false,
          }));
        } else {
          setUnbilledByClient((prev) => ({
            ...prev,
            error: result?.message || "Failed to fetch unbilled by client",
            isLoading: false,
          }));
        }
      } catch (error) {
        setUnbilledByClient((prev) => ({
          ...prev,
          error: error.message || "Error fetching unbilled by client",
          isLoading: false,
        }));
      }
    },
    [filters, executeAction],
  );

  // ===== YTD INVOICING =====
  const getYtdInvoicing = useCallback(
    async (customFilters = null) => {
      setYtdInvoicing((prev) => ({ ...prev, isLoading: true, error: null }));
      try {
        const filterParams = customFilters || filters;
        const result = await executeAction({
          entityName: "Dashboard",
          action: "ytdInvoicing",
          data: filterParams,
        });

        if (result?.isSuccessful && result?.data) {
          setYtdInvoicing((prev) => ({
            ...prev,
            data: result.data || [],
            isLoading: false,
          }));
        } else {
          setYtdInvoicing((prev) => ({
            ...prev,
            error: result?.message || "Failed to fetch YTD invoicing",
            isLoading: false,
          }));
        }
      } catch (error) {
        setYtdInvoicing((prev) => ({
          ...prev,
          error: error.message || "Error fetching YTD invoicing",
          isLoading: false,
        }));
      }
    },
    [filters, executeAction],
  );

  // ===== GET CALLS (detail) =====
  const getCalls = useCallback(
    async (customFilters = null, pageNumber = 1, recordsPerPage = 25) => {
      setCallsDetail((prev) => ({ ...prev, isLoading: true, error: null }));
      try {
        const filterParams = {
          ...(customFilters || filters),
          pageNumber,
          recordsPerPage,
        };
        const result = await executeAction({
          entityName: "Dashboard",
          action: "getCalls",
          data: filterParams,
        });

        if (result?.isSuccessful && result?.data) {
          setCallsDetail((prev) => ({
            ...prev,
            data: result.data?.data || [],
            page: result.data?.page || pageNumber,
            pageSize: result.data?.pageSize || recordsPerPage,
            total: result.data?.total || 0,
            isLoading: false,
          }));
        } else {
          setCallsDetail((prev) => ({
            ...prev,
            error: result?.message || "Failed to fetch calls detail",
            isLoading: false,
          }));
        }
      } catch (error) {
        setCallsDetail((prev) => ({
          ...prev,
          error: error.message || "Error fetching calls detail",
          isLoading: false,
        }));
      }
    },
    [filters, executeAction],
  );

  // ===== GET INVOICES (detail) =====
  const getInvoices = useCallback(
    async (customFilters = null, pageNumber = 1, recordsPerPage = 25) => {
      setInvoicesDetail((prev) => ({ ...prev, isLoading: true, error: null }));
      try {
        const filterParams = {
          ...(customFilters || filters),
          pageNumber,
          recordsPerPage,
        };
        const result = await executeAction({
          entityName: "Dashboard",
          action: "getInvoices",
          data: filterParams,
        });

        if (result?.isSuccessful && result?.data) {
          setInvoicesDetail((prev) => ({
            ...prev,
            data: result.data?.data || [],
            page: result.data?.page || pageNumber,
            pageSize: result.data?.pageSize || recordsPerPage,
            total: result.data?.total || 0,
            isLoading: false,
          }));
        } else {
          setInvoicesDetail((prev) => ({
            ...prev,
            error: result?.message || "Failed to fetch invoices detail",
            isLoading: false,
          }));
        }
      } catch (error) {
        setInvoicesDetail((prev) => ({
          ...prev,
          error: error.message || "Error fetching invoices detail",
          isLoading: false,
        }));
      }
    },
    [filters, executeAction],
  );

  // Refresh all metrics when filters change
  useEffect(() => {
    // Debounce to avoid multiple rapid calls
    const timer = setTimeout(() => {
      getUnbilledByClient();
      getYtdInvoicing();
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  return {
    // Filters
    filters,
    updateFilters,

    // Metrics
    unbilledByClient,
    ytdInvoicing,

    // Detail data
    callsDetail,
    invoicesDetail,

    // Actions
    getUnbilledByClient,
    getYtdInvoicing,
    getCalls,
    getInvoices,

    // Utility: check if any metric is loading
    isLoading: unbilledByClient.isLoading || ytdInvoicing.isLoading,

    // Utility: check if any metric has error
    hasError: unbilledByClient.error || ytdInvoicing.error,
  };
};

export default useClientDashboard;
